import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Card,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { BaseTable } from "src/components/BaseTable/BaseTable";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { useUpiTransactions, useTransactions } from "../transactions.hook";
import { upiTransactionsColumns } from "./UpiTransactions.constants";
import { CommonSearchbar } from "src/layouts/dashboard/Searchbar";

// ----------------------------------------------------------------------

const StyledListCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.info.light}40`,
  background: `linear-gradient(135deg, ${theme.palette.info.lighter}08 0%, ${theme.palette.info.light}05 100%)`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 25px ${theme.palette.info.light}20`,
    border: `1px solid ${theme.palette.info.light}60`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, ${theme.palette.info.main} 0%, ${theme.palette.info.light} 100%)`,
    borderRadius: `${theme.shape.borderRadiusMd}px ${theme.shape.borderRadiusMd}px 0 0`,
  },
}));

const MetricCard = styled(Box)(({ theme, color }) => {
  const getCardColors = () => {
    switch (color) {
      case 'success':
        return {
          main: theme.palette.success.main,
          light: theme.palette.success.light,
          lighter: theme.palette.success.lighter,
        };
      case 'warning':
        return {
          main: theme.palette.warning.main,
          light: theme.palette.warning.light,
          lighter: theme.palette.warning.lighter,
        };
      case 'error':
        return {
          main: theme.palette.error.main,
          light: theme.palette.error.light,
          lighter: theme.palette.error.lighter,
        };
      case 'info':
        return {
          main: theme.palette.info.main,
          light: theme.palette.info.light,
          lighter: theme.palette.info.lighter,
        };
      default:
        return {
          main: theme.palette.primary.main,
          light: theme.palette.primary.light,
          lighter: theme.palette.primary.lighter,
        };
    }
  };

  const colors = getCardColors();

  return {
    padding: theme.spacing(2.5),
    borderRadius: theme.shape.borderRadius * 1.5,
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${colors.lighter}15 50%, ${colors.light}08 100%)`,
    border: `1px solid ${colors.light}30`,
    textAlign: 'center',
    height: '100%',
    minHeight: '120px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 25px ${colors.light}20`,
      border: `1px solid ${colors.light}50`,
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: `linear-gradient(90deg, ${colors.main} 0%, ${colors.light} 100%)`,
      borderRadius: `${theme.shape.borderRadius * 1.5}px ${theme.shape.borderRadius * 1.5}px 0 0`,
    },
  };
});

export const UpiTransactionsTab = () => {
  const team = useSelectedTeam();
  const teamId = team?.teamId;
  const [dateRange, setDateRange] = useState("today");
  const [searchText, setSearchText] = useState("");
  const [tableData, setTableData] = useState([]);
  const isMountedRef = useRef(true);

  // Memoize the teamId to prevent unnecessary re-renders
  const currentTeamId = useMemo(() => {
    return teamId;
  }, [teamId]);

  const { data = {}, isFetching, error } = useUpiTransactions({
    teamId: currentTeamId,
    dateRange: dateRange,
    enabled: !!currentTeamId, // Only fetch if we have a teamId
  });

  // Fetch main transactions data to get transaction IDs
  const { data: mainTransactionsData = [], isFetching: isMainTransactionsFetching } = useTransactions({
    teamId: currentTeamId,
  });

  // Extract transactions from the response - handle different response structures
  const upiTransactions = useMemo(() => {
    // Handle different possible response structures
    if (Array.isArray(data)) {
      return data;
    } else if (data && Array.isArray(data.transactions)) {
      return data.transactions;
    } else if (data && Array.isArray(data.data)) {
      return data.data;
    } else {
      return [];
    }
  }, [data]);

  // Merge UPI transactions with main transactions data to get transaction IDs
  const transactions = useMemo(() => {
    if (!upiTransactions.length || !mainTransactionsData.length) {
      return upiTransactions;
    }

    // Create a map of paymentId to transactionId from main transactions
    const paymentToTransactionMap = new Map();
    mainTransactionsData.forEach(transaction => {
      if (transaction.paymentId) {
        paymentToTransactionMap.set(transaction.paymentId, transaction.transactionId);
      }
    });

    // Merge UPI transactions with transaction IDs and stop time
    return upiTransactions.map(upiTransaction => {
      const matchingTransaction = mainTransactionsData.find(transaction => 
        transaction.paymentId === upiTransaction.id
      );
      
      return {
        ...upiTransaction,
        transactionId: paymentToTransactionMap.get(upiTransaction.id) || "-",
        stoptime: matchingTransaction?.stoptime || null
      };
    });
  }, [upiTransactions, mainTransactionsData]);
  
      // Create filtered transactions for both statistics and table data
  const filteredTransactions = useMemo(() => {
    if (!transactions.length) return [];
    
    // Apply search text filtering only (date filtering is done server-side)
    if (!searchText.trim()) return transactions;
    
    const search = searchText.trim().toLowerCase();
    return transactions.filter((item) => {
      const fields = [
        "id",
        "vpa",
        "description",
        "status",
        "method",
        "currency",
      ];

      return fields.some((key) => {
        const val = item[key];
        const content = val?.toString().toLowerCase();
        return content?.includes(search);
      }) || 
      // Also search in nested fields
      (item.notes?.chargerId && item.notes.chargerId.toLowerCase().includes(search)) ||
      (item.notes?.connectorId && item.notes.connectorId.toString().includes(search)) ||
      (item.email && item.email.toLowerCase().includes(search)) ||
      (item.contact && item.contact.includes(search));
    });
  }, [transactions, searchText]);

  // Calculate simplified statistics from filtered transactions
  const statistics = useMemo(() => {
    const totalTransactions = filteredTransactions.length;
    
    if (totalTransactions === 0) {
      return {
        totalTransactions: 0,
        refundedTransactions: 0,
        failedTransactions: 0,
        totalCapturedAmount: 0,
        totalRefundedAmount: 0
      };
    }
    
    const refundedTransactions = filteredTransactions.filter(txn => txn.status === 'refunded').length;
    const failedTransactions = filteredTransactions.filter(txn => txn.status === 'failed').length;
    
    // Calculate total captured amount (successful transactions)
    const totalCapturedAmount = filteredTransactions
      .filter(txn => txn.status === 'captured')
      .reduce((sum, txn) => sum + (txn.amount || 0), 0);
    
    // Calculate total refunded amount from transactions that have actual refunds
    const totalRefundedAmount = filteredTransactions
      .reduce((sum, txn) => {
        // Only include if transaction has been refunded and refund_status is not "full"
        let refundAmount = 0;
        if (txn.amount_refunded && txn.amount_refunded > 0 && txn.refund_status !== "full") {
          refundAmount = txn.amount_refunded;
        } else if (txn.status === 'refunded' && txn.amount && txn.refund_status !== "full") {
          refundAmount = txn.amount;
        }
        return sum + refundAmount;
      }, 0);

    return {
      totalTransactions,
      refundedTransactions,
      failedTransactions,
      totalCapturedAmount,
      totalRefundedAmount
    };
  }, [filteredTransactions]);

  const dateRangeOptions = [
    { value: "today", label: "Today" },
    { value: "last2days", label: "Last 2 Days" },
  ];

  const handleDateRangeChange = useCallback((event) => {
    const value = event.target.value;
    setDateRange(value);
  }, []);



  const handleSearching = useCallback((event) => {
    setSearchText(event.target.value);
  }, []);

  // Update tableData with filtered and sorted transactions
  useEffect(() => {
    if (!isMountedRef.current) return;
    
    if (!isFetching && currentTeamId) {
      if (filteredTransactions.length > 0) {
        let finalFiltered = [...filteredTransactions];

        // Sort by date with latest transactions at the top
        finalFiltered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        
        // Update table data with final filtered and sorted transactions
        setTableData(finalFiltered);
      } else {
        setTableData([]);
      }
    }
  }, [isFetching, currentTeamId, filteredTransactions]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Early return if no teamId - placed after all hooks
  if (!currentTeamId) {
    return (
      <Box>
        <Typography variant="body2" color="warning.main">
          Please select a team to view UPI transactions
        </Typography>
      </Box>
    );
  }

  // Removed row click redirect - only payment ID has hyperlink now

  return (
    <Box>
      {error && (
        <Card sx={{ p: 2, mb: 2 }}>
          <Typography variant="body2" color="error.main">
            Unable to load UPI transactions: {error.message || "Something went wrong. Please try again."}
          </Typography>
        </Card>
      )}

      



      {/* UPI Transactions Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard color="success">
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.dark', mb: 1 }}>
              Total Transactions
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.dark' }}>
              {statistics.totalTransactions}
            </Typography>
          </MetricCard>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard color="warning">
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'warning.dark', mb: 1 }}>
              Total Refunded
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.dark' }}>
              {statistics.refundedTransactions}
            </Typography>
          </MetricCard>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard color="error">
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.dark', mb: 1 }}>
              Total Failed
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.dark' }}>
              {statistics.failedTransactions}
            </Typography>
          </MetricCard>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard color="success">
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.dark', mb: 1 }}>
              Total Captured Amount
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.dark' }}>
              ₹{(() => {
                const amountInPaise = statistics.totalCapturedAmount;
                const amountInRupees = amountInPaise / 100;
                
                if (amountInPaise >= 100000) {
                  return `${(amountInPaise / 100000).toFixed(1)}K`;
                } else {
                  return amountInRupees.toFixed(2);
                }
              })()}
            </Typography>
          </MetricCard>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard color="error">
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.dark', mb: 1 }}>
              Total Refunded Amount
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.dark' }}>
              ₹{(() => {
                const amountInPaise = statistics.totalRefundedAmount;
                const amountInRupees = amountInPaise / 100;
                
                if (amountInPaise >= 100000) {
                  return `${(amountInPaise / 100000).toFixed(1)}K`;
                } else {
                  return amountInRupees.toFixed(2);
                }
              })()}
            </Typography>
          </MetricCard>
        </Grid>
      </Grid>

      <StyledListCard sx={{ width: "103%" }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <CommonSearchbar placeholder="Search Payments" searchFunc={handleSearching} />
          
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel id="date-range-select-label">Date Range</InputLabel>
            <Select
              labelId="date-range-select-label"
              value={dateRange}
              label="Date Range"
              onChange={handleDateRangeChange}
            >
              {dateRangeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        <BaseTable
          rows={tableData ?? []}
          columns={upiTransactionsColumns}
          loading={isFetching || isMainTransactionsFetching}
          getRowId={(row) => row.id}
          pageSize={10} // Fixed page size for client-side pagination
          showExportCsvBtn={true}
          csvMorphemes="upi-transactions"
          csvColumns={upiTransactionsColumns}
          sx={{
            ".MuiDataGrid-columnHeaderTitle": {
              overflow: "hidden !important",
              lineHeight: "20px !important",
              whiteSpace: "normal !important",
            },
          }}
        />
      </StyledListCard>
    </Box>
  );
};

